import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Building2, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ArticleDetail = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/clinic/articles/${articleId}/public`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [articleId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin w-12 h-12 border-4 border-[#0596DE] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) {
    navigate('/blog', { replace: true });
    return null;
  }

  const { article, clinic } = data;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to={`/clinic/${clinic.slug}`} className="inline-flex items-center gap-2 text-[#0596DE] hover:underline mb-6 text-sm">
          <ArrowLeft className="w-4 h-4" /> Retour à {clinic.name}
        </Link>

        {article.cover_url && (
          <div className="aspect-video rounded-2xl overflow-hidden mb-8 bg-[#F1F5F9]">
            <img src={article.cover_url} alt={article.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="flex items-center gap-3 text-sm text-[#64748B] mb-3">
          <span className="flex items-center gap-1.5">
            <Building2 className="w-4 h-4" /> {clinic.name}
          </span>
          <span className="flex items-center gap-1.5">
            <Calendar className="w-4 h-4" /> {new Date(article.created_at).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
        </div>

        <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6 leading-tight">{article.title}</h1>

        {article.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {article.tags.map((t, i) => (
              <span key={i} className="text-xs px-3 py-1 bg-[#E0F2FE] text-[#0596DE] rounded-full">#{t}</span>
            ))}
          </div>
        )}

        <div className="prose prose-lg max-w-none text-[#475569] leading-relaxed whitespace-pre-line">
          {article.content}
        </div>

        <div className="mt-12 p-6 bg-gradient-to-br from-[#0596DE] to-[#047CBA] rounded-2xl text-white">
          <h3 className="text-xl font-bold mb-2">Une question ? Prenez rendez-vous</h3>
          <p className="text-white/80 text-sm mb-4">Notre équipe est à votre écoute.</p>
          <div className="flex flex-wrap gap-3">
            <Link to={`/clinic/${clinic.slug}`}>
              <Button className="bg-white text-[#0596DE] hover:bg-white/90 rounded-full">
                Voir la clinique
              </Button>
            </Link>
            {clinic.phone && (
              <a href={`tel:${clinic.phone}`} className="bg-white/10 border border-white/30 px-5 py-2 rounded-full font-medium hover:bg-white/20 transition flex items-center gap-2">
                <Phone className="w-4 h-4" /> {clinic.phone}
              </a>
            )}
          </div>
        </div>
      </article>
    </div>
  );
};

export default ArticleDetail;
